import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import HubMedMonthlySummary from "@/components/admin/hubmed/HubMedMonthlySummary";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const fmtCell = (v) =>
  typeof v === "number" ? v.toFixed(2) : v == null ? "" : String(v);

function buildCsv(rows) {
  const headers = [
    "Date Created",
    "First Name",
    "Last Name",
    "Appointment Type",
    "Provider Order ID",
    "Pharmacy",
    "Status",
    "Invoice Number",
    "Paid Amount",
    "Rebate",
  ];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.dateCreated,
        r.firstName,
        r.lastName,
        r.appointmentType,
        r.providerOrderId,
        r.pharmacy,
        r.status,
        r.invoiceNumber,
        fmtCell(r.paidAmount),
        fmtCell(r.rebate),
      ]
        .map(escape)
        .join(",")
    ),
  ];
  return lines.join("\r\n");
}

function downloadCsv(csv, month, year) {
  const monthName = MONTHS[month - 1];
  const filename = `HubMed_Appointments_${monthName}_${year}.csv`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Per-invoice row states shown in the search modal.
const STATE_ICON = {
  pending: <RemoveCircleOutlineIcon color="disabled" fontSize="small" />,
  searching: <CircularProgress size={16} />,
  paid: <CheckCircleIcon color="success" fontSize="small" />,
  unpaid: <RemoveCircleOutlineIcon color="warning" fontSize="small" />,
  error: <ErrorOutlineIcon color="error" fontSize="small" />,
};

const stateLabel = (item) => {
  switch (item.state) {
    case "pending":
      return "Waiting…";
    case "searching":
      return "Searching Halaxy…";
    case "paid":
      return `Paid $${Number(item.totalPaid).toFixed(2)} — saved`;
    case "unpaid":
      return "No payment recorded yet";
    case "error":
      return `Error: ${item.message || "failed"}`;
    default:
      return "";
  }
};

const HubMedReportsPage = () => {
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [phase, setPhase] = useState("searching"); // searching | building | done
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState(0); // completed count

  const patchItem = (consultId, patch) =>
    setItems((prev) =>
      prev.map((it) => (it.consultId === consultId ? { ...it, ...patch } : it))
    );

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Which invoices in this month still read $0 and need a Halaxy re-check?
      const pendingResp = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_APPOINTMENT_REPORT_PENDING_INVOICES,
        "GET",
        null,
        { month, year }
      );
      const pending = Array.isArray(pendingResp?.invoices)
        ? pendingResp.invoices
        : [];

      if (pending.length > 0) {
        // 2. Show the modal and search each invoice one at a time.
        setItems(
          pending.map((p) => ({
            consultId: p.consultId,
            invoiceNumber: p.invoiceNumber,
            name: `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || "—",
            state: "pending",
            totalPaid: null,
            message: null,
          }))
        );
        setProgress(0);
        setPhase("searching");
        setModalOpen(true);

        for (let i = 0; i < pending.length; i++) {
          const p = pending[i];
          patchItem(p.consultId, { state: "searching" });
          try {
            const res = await phappsRequest(
              API_PATHS.HUBMED_ADMIN_REFRESH_INVOICE,
              "POST",
              { consultId: p.consultId, invoiceNumber: p.invoiceNumber }
            );
            patchItem(p.consultId, {
              state: res?.updated ? "paid" : "unpaid",
              totalPaid: res?.totalPaid ?? null,
              message: res?.reason ?? null,
            });
          } catch (err) {
            patchItem(p.consultId, { state: "error", message: err?.message });
          }
          setProgress(i + 1);
        }
        setPhase("building");
      }

      // 3. Build and download the report (reads the now-updated paid amounts).
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_APPOINTMENT_REPORT,
        "GET",
        null,
        { month, year }
      );
      if (!data?.rows?.length) {
        setError("No appointments found for the selected month.");
        setModalOpen(false);
        return;
      }
      const csv = buildCsv(data.rows);
      downloadCsv(csv, month, year);
      if (pending.length > 0) {
        setPhase("done");
      }
    } catch (err) {
      setError(err?.message ?? "Failed to download report. Please try again.");
      setModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const total = items.length;
  const modalBusy = phase === "searching" || phase === "building";

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5" fontWeight="bold" mb={1} className="hm-no-print">
        Appointment Report
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} className="hm-no-print">
        Download a CSV of all HubMed appointments for a given month. Any invoice
        still reading $0 is re-checked against Halaxy first.
      </Typography>

      <Box
        sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap", mb: 3 }}
        className="hm-no-print"
      >
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={month}
            label="Month"
            onChange={(e) => setMonth(e.target.value)}
          >
            {MONTHS.map((name, i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={(e) => setYear(e.target.value)}
          >
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={loading}
        >
          {loading ? "Working…" : "Download Appointment Report"}
        </Button>
      </Box>

      {error && (
        <Alert
          severity={error.startsWith("No appointments") ? "info" : "error"}
          className="hm-no-print"
        >
          {error}
        </Alert>
      )}

      <HubMedMonthlySummary month={month} year={year} />

      <Dialog
        open={modalOpen}
        onClose={modalBusy ? undefined : () => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {phase === "building"
            ? "Generating report…"
            : phase === "done"
            ? "Report downloaded"
            : "Re-checking invoices against Halaxy"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant={
                phase === "building" ? "indeterminate" : "determinate"
              }
              value={total ? (progress / total) * 100 : 0}
            />
            <Typography variant="caption" color="text.secondary">
              {phase === "building"
                ? "Building the report…"
                : `${progress} of ${total} invoice(s) checked`}
            </Typography>
          </Box>
          <List dense sx={{ maxHeight: 340, overflowY: "auto" }}>
            {items.map((it) => (
              <ListItem key={it.consultId} disableGutters>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  {STATE_ICON[it.state]}
                </ListItemIcon>
                <ListItemText
                  primary={`${it.name} · Invoice ${it.invoiceNumber}`}
                  secondary={stateLabel(it)}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={modalBusy}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HubMedReportsPage;
